import { memo, useState, useEffect, useRef, useCallback, Suspense, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings as SettingsIcon,
  X,
  Palette,
  Volume2,
  Sparkles,
  Bell,
  FileText,
  Trophy,
  Globe,
  Heart,
  ChevronRight,
  ChevronLeft,
  User,
  LogOut,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDeviceType } from '../../hooks/useDeviceType';
import { useMouseActivity } from '../../hooks/useMouseActivity';
import { useSettingsStore } from '../../store/useSettingsStore';
import { BACKGROUNDS } from '../../data/constants';
import { ROLE_EMOJI_ELF, ROLE_EMOJI_HUMAN } from '../../data/levels';
import { useAuth } from '../../contexts/AuthContext';
import { updateUsernameSecure, checkUsernameAvailability } from '../../lib/userSyncAuth';

import { showGameToast } from '../ui/GameToast';
import { MusicCreditsModal } from '../lazy';
import { LoadingSpinner } from '../LoadingSpinner';
import { SettingsContent } from './SettingsContent';
import { ScrollArea } from '../ui/scroll-area';
import type { Track } from '../../types';
import lofiTracks from '../../data/lofi.json';
import synthwaveTracks from '../../data/synthwave.json';
import { Popover, PopoverTrigger, PopoverContent, PopoverBody } from '../ui/popover';
import { createRateLimiter } from '../../utils/rateLimiters';

const tabs = [
  { id: 'timer', label: 'Timer', icon: SettingsIcon },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'sounds', label: 'Sounds', icon: Volume2 },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'timezone', label: 'Timezone', icon: Globe },
  { id: 'progress', label: 'Progress', icon: Trophy },
  { id: 'account', label: 'Account', icon: User },
  { id: 'music', label: 'Credits', icon: FileText },
  { id: 'whats-new', label: "What's New", icon: Sparkles },
  { id: 'support', label: 'Support Us', icon: Heart },
] as const;

export const SettingsPopover = memo(function SettingsPopover() {
  const isMouseActive = useMouseActivity(8000);
  const { appUser, user, signOut } = useAuth();
  const isSettingsOpen = useSettingsStore((state) => state.isSettingsOpen);
  const setSettingsOpen = useSettingsStore((state) => state.setSettingsOpen);

  // Restore missing store data
  const {
    timers,
    setPomodoroDuration,
    setShortBreakDuration,
    setLongBreakDuration,
    pomodorosBeforeLongBreak,
    setPomodorosBeforeLongBreak,
    autoStartBreaks,
    setAutoStartBreaks,
    autoStartPomodoros,
    setAutoStartPomodoros,
    soundEnabled,
    setSoundEnabled,
    volume,
    setVolume,
    levelSystemEnabled,
    setLevelSystemEnabled,
    background,
    setBackground,
    autoHideUI,
    setAutoHideUI,
    musicVolume,
    setMusicVolume,
    ambientVolumes,
    setAmbientVolume,
    playlist,
    setPlaylist,
    level,
    xp,
    prestigeLevel,
    totalPomodoros,
    totalStudyMinutes,
    levelPath,
    setLevelPath,
    username,
    setUsername,
    resetProgress,
    totalUniqueDays,
    consecutiveLoginDays,
    pomodoroBoostActive,
    pomodoroBoostExpiresAt,
    firstLoginDate,
    timezone,
    weekendDays,
    pendingTimezone,
    pendingTimezoneAppliesAt,
    lastTimezoneChangeAt,
    isTourActive,
  } = useSettingsStore();

  const [activeTab, setActiveTab] = useState<
    | 'timer'
    | 'appearance'
    | 'sounds'
    | 'notifications'
    | 'timezone'
    | 'music'
    | 'progress'
    | 'account'
    | 'whats-new'
    | 'support'
  >('timer');
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

  const [roleChangeMessage, setRoleChangeMessage] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [showMusicCredits, setShowMusicCredits] = useState(false);
  const [canClose, setCanClose] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    () => {
      if (typeof window === 'undefined' || typeof Notification === 'undefined') {
        return 'default';
      }
      return Notification.permission;
    }
  );
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const rateLimiterRef = useRef(createRateLimiter(720000)); // 12 minutes (5 changes per hour)
  const { isPortrait } = useDeviceType();

  // No manual scaling needed anymore - handled globally by ScaleWrapper
  const scaleClass = '';

  // Use User Agent-based detection combined with window width for DevTools support
  // Memoized to prevent recalculation on every render
  const { isMobile: isScreenMobile } = useDeviceType(768);

  const showDesktopLayout = useMemo(() => {
    if (typeof window === 'undefined') return true;
    const ua = navigator.userAgent.toLowerCase();
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
    // Show mobile layout if UA is mobile OR screen width is mobile-sized
    return !isMobileUA && !isScreenMobile;
  }, [isScreenMobile]);

  // Calculate total track count
  const totalTracks = lofiTracks.length + synthwaveTracks.length;

  // Listen for notification permission changes
  useEffect(() => {
    const handlePermissionChange = () => {
      if ('Notification' in window) {
        setNotificationPermission(Notification.permission);
      }
    };
    window.addEventListener('notificationPermissionChange', handlePermissionChange);
    return () => window.removeEventListener('notificationPermissionChange', handlePermissionChange);
  }, []);

  // Auto-dismiss role change message
  useEffect(() => {
    if (roleChangeMessage) {
      const timer = setTimeout(() => {
        setRoleChangeMessage(null);
        setMobileView('list');
        setSettingsOpen(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [roleChangeMessage, setSettingsOpen]);

  // Prevent immediate closing on mobile (ghost clicks)
  useEffect(() => {
    if (isSettingsOpen) {
      const timer = setTimeout(() => setCanClose(true), 300);
      return () => clearTimeout(timer);
    } else {
      setCanClose(false);
    }
  }, [isSettingsOpen]);

  // Focus management: focus modal when opened, return focus when closed
  useEffect(() => {
    if (isSettingsOpen) {
      modalRef.current?.focus();
    } else {
      triggerButtonRef.current?.focus();
    }
  }, [isSettingsOpen]);

  // Keyboard shortcuts (moved after handleSave to avoid stale closure)

  const handleRoleChange = (newRole: 'elf' | 'human') => {
    rateLimiterRef.current(() => {
      setLevelPath(newRole);

      const messages = {
        elf: [
          'You have chosen the path of the Elf! May nature guide your journey.',
          'The forest welcomes you, brave Elf. Your adventure begins anew!',
          'An Elf emerges! The ancient woods await your wisdom.',
          'You walk the Elven path. Grace and focus shall be your companions.',
        ],
        human: [
          'You have chosen the path of the Human! May courage light your way.',
          "A warrior's path chosen! Your legend starts now, brave Human.",
          'The Human spirit awakens within you. Face your challenges head-on!',
          'You walk the Human path. Strength and determination guide you forward.',
        ],
      };

      const randomMessage = messages[newRole][Math.floor(Math.random() * messages[newRole].length)];
      setRoleChangeMessage(randomMessage);
    })();
  };

  // Filter backgrounds based on viewport orientation (portrait vs landscape)
  const targetOrientation = isPortrait ? 'vertical' : 'horizontal';
  const filteredBackgrounds = BACKGROUNDS.filter((bg) => bg.orientation === targetOrientation);

  // Temporary state for settings (only applied on Save)
  const [tempTimers, setTempTimers] = useState(timers);
  const [tempPomodorosBeforeLongBreak, setTempPomodorosBeforeLongBreak] =
    useState(pomodorosBeforeLongBreak);
  const [tempAutoStartBreaks, setTempAutoStartBreaks] = useState(autoStartBreaks);
  const [tempAutoStartPomodoros, setTempAutoStartPomodoros] = useState(autoStartPomodoros);
  const [tempSoundEnabled, setTempSoundEnabled] = useState(soundEnabled);
  const [tempVolume, setTempVolume] = useState(volume);
  const [tempMusicVolume, setTempMusicVolume] = useState(musicVolume);
  const [tempAmbientVolumes, setTempAmbientVolumes] = useState(ambientVolumes);
  const [tempBackground, setTempBackground] = useState(background);
  const [tempAutoHideUI, setTempAutoHideUI] = useState(autoHideUI);
  const [tempLevelSystemEnabled, setTempLevelSystemEnabled] = useState(levelSystemEnabled);
  const [tempPlaylist, setTempPlaylist] = useState(playlist);
  const [usernameInput, setUsernameInput] = useState(username);

  // Check for unsaved changes
  const hasUnsavedChanges =
    JSON.stringify(tempTimers) !== JSON.stringify(timers) ||
    tempPomodorosBeforeLongBreak !== pomodorosBeforeLongBreak ||
    tempAutoStartBreaks !== autoStartBreaks ||
    tempAutoStartPomodoros !== autoStartPomodoros ||
    tempSoundEnabled !== soundEnabled ||
    tempVolume !== volume ||
    tempMusicVolume !== musicVolume ||
    JSON.stringify(tempAmbientVolumes) !== JSON.stringify(ambientVolumes) ||
    tempBackground !== background ||
    tempAutoHideUI !== autoHideUI ||
    tempLevelSystemEnabled !== levelSystemEnabled ||
    tempPlaylist !== playlist;

  // Validation check
  const isInvalid =
    tempTimers.pomodoro < 1 ||
    tempTimers.pomodoro > 60 ||
    tempTimers.shortBreak < 1 ||
    tempTimers.shortBreak > 30 ||
    tempTimers.longBreak < 1 ||
    tempTimers.longBreak > 30;

  // Reset view and tab ONLY when modal opens (not on every store change)
  useEffect(() => {
    if (isSettingsOpen) {
      setActiveTab('timer'); // Default to General tab
      setMobileView('list'); // Reset to list view on open
    }
  }, [isSettingsOpen]);

  // Sync temporary state with store values when modal is open
  useEffect(() => {
    if (isSettingsOpen) {
      setTempTimers(timers);
      setTempPomodorosBeforeLongBreak(pomodorosBeforeLongBreak);
      setTempAutoStartBreaks(autoStartBreaks);
      setTempAutoStartPomodoros(autoStartPomodoros);
      setTempSoundEnabled(soundEnabled);
      setTempVolume(volume);
      setTempMusicVolume(musicVolume);
      setTempAmbientVolumes(ambientVolumes);
      setTempBackground(background);
      setTempAutoHideUI(autoHideUI);
      setTempLevelSystemEnabled(levelSystemEnabled);
      setTempPlaylist(playlist);
      setUsernameInput(username);
    }
  }, [
    isSettingsOpen,
    timers,
    pomodorosBeforeLongBreak,
    autoStartBreaks,
    autoStartPomodoros,
    soundEnabled,
    volume,
    musicVolume,
    ambientVolumes,
    background,
    levelSystemEnabled,
    playlist,
    username,
    autoHideUI,
  ]);

  const handleSaveUsername = async () => {
    if (!appUser) {
      setUsernameError('You must be logged in to change username');
      return;
    }

    if (!usernameInput || usernameInput.trim().length === 0) {
      setUsernameError('Username cannot be empty');
      return;
    }

    if (usernameInput.length > 20) {
      setUsernameError('Username cannot exceed 20 characters');
      return;
    }

    if (usernameInput === username) {
      setUsernameError('That is already your username');
      return;
    }

    setUsernameError(null);
    setUsernameLoading(true);

    try {
      // Check availability first (client-side check for better UX)
      const isAvailable = await checkUsernameAvailability(usernameInput);
      if (!isAvailable) {
        setUsernameError('Username is already taken');
        setUsernameLoading(false);
        return;
      }

      // Try free update first (let server decide if cooldown passed)
      const updatedUser = await updateUsernameSecure(appUser.id, usernameInput, false);

      // Success - update local Zustand store with new username and timestamp
      setUsername(usernameInput);

      if (import.meta.env.DEV) {
        console.log('[Settings] Username updated successfully (free):', updatedUser.username);
      }
      toast.success('Username updated successfully!');
    } catch (error) {
      console.error('[Settings] Error updating username:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // Check if it's a cooldown error and user has enough XP
      if (errorMessage.includes('cooldown') && xp >= 50) {
        // Extract wait time from error message for better UX
        const waitTimeMatch = errorMessage.match(/Wait ([\d.]+) more hours/);
        const hours = waitTimeMatch ? waitTimeMatch[1] : 'several';

        // Show toast with action button to pay 50 XP
        toast('Username change is on cooldown', {
          description: `${hours} hours remaining. You can pay 50 XP to change now.`,
          duration: 10000,
          action: {
            label: 'Pay 50 XP',
            onClick: async () => {
              try {
                setUsernameLoading(true);
                // Retry with XP payment
                const updatedUser = await updateUsernameSecure(appUser.id, usernameInput, true);

                // Success - update local Zustand store with new username, XP, and timestamp
                setUsername(usernameInput, true);

                if (import.meta.env.DEV) {
                  console.log(
                    '[Settings] Username updated successfully (50 XP cost):',
                    updatedUser.username,
                    'XP:',
                    updatedUser.xp
                  );
                }
                toast.success('Username updated! 50 XP deducted.');
                showGameToast('-50 XP Spent');
              } catch (retryError) {
                console.error('[Settings] Error updating username with XP:', retryError);
                const retryMessage =
                  retryError instanceof Error ? retryError.message : 'Unknown error';
                setUsernameError(retryMessage);
                toast.error(retryMessage);
              } finally {
                setUsernameLoading(false);
              }
            },
          },
          cancel: {
            label: 'Cancel',
            onClick: () => {},
          },
        });
      } else if (errorMessage.includes('cooldown')) {
        // Cooldown error but insufficient XP
        const waitTimeMatch = errorMessage.match(/Wait ([\d.]+) more hours/);
        const hours = waitTimeMatch ? waitTimeMatch[1] : 'several';
        setUsernameError(
          `Username change is on cooldown (${hours} hours remaining). You need 50 XP to change early.`
        );
      } else if (errorMessage.includes('Insufficient XP')) {
        setUsernameError('You do not have enough XP (need 50 XP)');
      } else if (errorMessage.includes('empty')) {
        setUsernameError('Username cannot be empty');
      } else if (errorMessage.includes('20 characters')) {
        setUsernameError('Username cannot exceed 20 characters');
      } else if (errorMessage.includes('already taken')) {
        setUsernameError('Username is already taken');
      } else {
        setUsernameError(`Failed to update username: ${errorMessage}`);
      }
    } finally {
      setUsernameLoading(false);
    }
  };

  const handleSave = useCallback(() => {
    // Apply all temporary settings to store
    setPomodoroDuration(Math.min(60, Math.max(1, Math.round(tempTimers.pomodoro))));
    setShortBreakDuration(Math.min(30, Math.max(1, Math.round(tempTimers.shortBreak))));
    setLongBreakDuration(Math.min(30, Math.max(1, Math.round(tempTimers.longBreak))));
    setPomodorosBeforeLongBreak(Math.round(tempPomodorosBeforeLongBreak));
    setAutoStartBreaks(tempAutoStartBreaks);
    setAutoStartPomodoros(tempAutoStartPomodoros);
    setSoundEnabled(tempSoundEnabled);
    setVolume(Math.round(tempVolume));
    setMusicVolume(Math.round(tempMusicVolume));
    setBackground(tempBackground);
    setAutoHideUI(tempAutoHideUI);
    setLevelSystemEnabled(tempLevelSystemEnabled);
    setPlaylist(tempPlaylist);

    // Apply ambient volumes
    Object.keys(tempAmbientVolumes).forEach((soundId) => {
      setAmbientVolume(soundId, Math.round(tempAmbientVolumes[soundId]));
    });

    // Don't close the modal; let user continue editing
  }, [
    tempTimers,
    tempPomodorosBeforeLongBreak,
    tempAutoStartBreaks,
    tempAutoStartPomodoros,
    tempSoundEnabled,
    tempVolume,
    tempMusicVolume,
    tempAmbientVolumes,
    tempBackground,
    tempAutoHideUI,
    tempLevelSystemEnabled,
    tempPlaylist,
    setPomodoroDuration,
    setShortBreakDuration,
    setLongBreakDuration,
    setPomodorosBeforeLongBreak,
    setAutoStartBreaks,
    setAutoStartPomodoros,
    setSoundEnabled,
    setVolume,
    setMusicVolume,
    setBackground,
    setAutoHideUI,
    setLevelSystemEnabled,
    setPlaylist,
    setAmbientVolume,
  ]);

  // Handle keyboard shortcuts (placed here to use handleSave without stale closure)
  useEffect(() => {
    if (!isSettingsOpen) return;

    const handleKeyboard = (e: KeyboardEvent) => {
      // Ignore keyboard shortcuts when typing in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Escape to close
      if (e.key === 'Escape') {
        setSettingsOpen(false);
        return;
      }

      // Cmd/Ctrl/Alt + S to save
      if ((e.metaKey || e.ctrlKey || e.altKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
        return;
      }

      // Number keys 1-7 to jump to tabs
      const numKey = parseInt(e.key);
      if (numKey >= 1 && numKey <= tabs.length) {
        setActiveTab(tabs[numKey - 1].id as typeof activeTab);
        return;
      }

      // Arrow keys to navigate tabs
      const currentIndex = tabs.findIndex((t) => t.id === activeTab);
      if (e.key === 'ArrowDown' && currentIndex < tabs.length - 1) {
        setActiveTab(tabs[currentIndex + 1].id as typeof activeTab);
      } else if (e.key === 'ArrowUp' && currentIndex > 0) {
        setActiveTab(tabs[currentIndex - 1].id as typeof activeTab);
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [isSettingsOpen, activeTab, handleSave, setSettingsOpen]);

  const handleReset = () => {
    // Reset temporary state to current store values
    setTempTimers(timers);
    setTempPomodorosBeforeLongBreak(pomodorosBeforeLongBreak);
    setTempAutoStartBreaks(autoStartBreaks);
    setTempAutoStartPomodoros(autoStartPomodoros);
    setTempSoundEnabled(soundEnabled);
    setTempVolume(volume);
    setTempMusicVolume(musicVolume);
    setTempAmbientVolumes(ambientVolumes);
    setTempBackground(background);
    setTempAutoHideUI(autoHideUI);
    setTempLevelSystemEnabled(levelSystemEnabled);
    setTempPlaylist(playlist);
    setUsernameInput(username);
  };

  // Trigger button component
  const trigger = (
    <motion.button
      data-tour="settings-button"
      ref={triggerButtonRef}
      onClick={(e) => {
        e.stopPropagation();
        setSettingsOpen(true);
      }}
      aria-label="Open settings"
      aria-hidden={!isMouseActive && autoHideUI}
      tabIndex={!isMouseActive && autoHideUI ? -1 : 0}
      animate={{
        opacity: isMouseActive || !autoHideUI || isTourActive ? 1 : 0,
      }}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
      className={`p-3 bg-black/40 backdrop-blur-md rounded-full text-white hover:bg-black/60 transition-colors border border-gray-400/60 ${!isMouseActive && autoHideUI ? 'pointer-events-none' : ''}`}
    >
      <SettingsIcon size={24} />
    </motion.button>
  );

  return (
    <>
      {/* Desktop: Popover */}
      {showDesktopLayout && (
        <Popover open={isSettingsOpen} onOpenChange={setSettingsOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent
            className={`bg-gray-900/95 backdrop-blur-xl border-white/10 rounded-2xl w-[594px] p-0 max-h-[85vh] z-[100] transition-opacity duration-500 ${scaleClass}`}
            align="end"
            side="bottom"
            sideOffset={8}
          >
            <PopoverBody className="p-0">
              <div ref={modalRef} tabIndex={-1} className="flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
                  <h2 className="text-lg font-bold text-white">Settings</h2>
                  <button
                    onClick={() => setSettingsOpen(false)}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors"
                    aria-label="Close settings"
                  >
                    <X size={20} className="text-white" />
                  </button>
                </div>

                {/* Sidebar + Content Layout */}
                <div className="flex flex-1 overflow-hidden min-w-0">
                  {/* Vertical Sidebar */}
                  <ScrollArea className="w-[160px] border-r border-white/10 shrink-0 h-full">
                    <div className="flex flex-col py-2 min-h-full">
                      {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isWhatsNew = tab.id === 'whats-new';
                        return (
                          <button
                            key={tab.id}
                            role="tab"
                            aria-selected={activeTab === tab.id}
                            aria-controls={`${tab.id}-panel`}
                            id={`${tab.id}-tab`}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full px-4 py-3 text-sm font-medium transition-colors flex items-center gap-3 relative ${
                              isWhatsNew ? 'mt-2 pt-5 border-t border-white/10' : ''
                            } ${
                              activeTab === tab.id
                                ? 'text-white bg-white/5'
                                : 'text-gray-400 hover:text-gray-300 hover:bg-white/5'
                            }`}
                          >
                            {activeTab === tab.id && (
                              <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-purple-500" />
                            )}
                            <Icon size={18} className="shrink-0" />
                            <span className="truncate">{tab.label}</span>
                          </button>
                        );
                      })}

                      {/* Sign Out Button */}
                      {appUser && !user?.is_anonymous && (
                        <div className="px-2 pt-2 mt-auto border-t border-white/10">
                          <button
                            onClick={async () => {
                              try {
                                await signOut();
                                setSettingsOpen(false);
                                toast.success('Signed out successfully');
                                // Force reload to ensure clean state
                                window.location.reload();
                              } catch (err) {
                                console.error('Sign out error:', err);
                                toast.error('Failed to sign out');
                              }
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors flex items-center gap-3"
                          >
                            <LogOut size={18} className="shrink-0" />
                            <span className="truncate">Sign Out</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </ScrollArea>

                  {/* Content - Scrollable */}
                  <ScrollArea className="flex-1 max-w-full">
                    <div className="p-6 w-full max-w-full overflow-hidden min-w-0">
                      <SettingsContent
                        activeTab={activeTab}
                        isMobile={false}
                        tempTimers={tempTimers}
                        setTempTimers={setTempTimers}
                        tempPomodorosBeforeLongBreak={tempPomodorosBeforeLongBreak}
                        setTempPomodorosBeforeLongBreak={setTempPomodorosBeforeLongBreak}
                        tempAutoStartBreaks={tempAutoStartBreaks}
                        setTempAutoStartBreaks={setTempAutoStartBreaks}
                        tempAutoStartPomodoros={tempAutoStartPomodoros}
                        setTempAutoStartPomodoros={setTempAutoStartPomodoros}
                        tempSoundEnabled={tempSoundEnabled}
                        setTempSoundEnabled={setTempSoundEnabled}
                        tempVolume={tempVolume}
                        setTempVolume={setTempVolume}
                        tempLevelSystemEnabled={tempLevelSystemEnabled}
                        setTempLevelSystemEnabled={setTempLevelSystemEnabled}
                        notificationPermission={notificationPermission}
                        tempBackground={tempBackground}
                        setTempBackground={setTempBackground}
                        tempAutoHideUI={tempAutoHideUI}
                        setTempAutoHideUI={setTempAutoHideUI}
                        filteredBackgrounds={filteredBackgrounds}
                        tempMusicVolume={tempMusicVolume}
                        setTempMusicVolume={setTempMusicVolume}
                        tempAmbientVolumes={tempAmbientVolumes}
                        setTempAmbientVolumes={setTempAmbientVolumes}
                        tempPlaylist={tempPlaylist}
                        setTempPlaylist={setTempPlaylist}
                        totalTracks={totalTracks}
                        setShowMusicCredits={setShowMusicCredits}
                        level={level}
                        xp={xp}
                        prestigeLevel={prestigeLevel}
                        totalPomodoros={totalPomodoros}
                        totalStudyMinutes={totalStudyMinutes}
                        levelPath={levelPath}
                        handleRoleChange={handleRoleChange}
                        usernameInput={usernameInput}
                        setUsernameInput={setUsernameInput}
                        usernameError={usernameError}
                        setUsernameError={setUsernameError}
                        usernameLoading={usernameLoading}
                        handleSaveUsername={handleSaveUsername}
                        resetProgress={resetProgress}
                        totalUniqueDays={totalUniqueDays}
                        consecutiveLoginDays={consecutiveLoginDays}
                        pomodoroBoostActive={pomodoroBoostActive}
                        pomodoroBoostExpiresAt={pomodoroBoostExpiresAt}
                        firstLoginDate={firstLoginDate}
                        timezone={timezone}
                        weekendDays={weekendDays}
                        pendingTimezone={pendingTimezone}
                        pendingTimezoneAppliesAt={pendingTimezoneAppliesAt}
                        lastTimezoneChangeAt={lastTimezoneChangeAt}
                      />
                    </div>
                  </ScrollArea>
                </div>

                {/* Footer */}
                <div className="border-t border-white/10 shrink-0">
                  {hasUnsavedChanges && (
                    <div className="px-4 pt-3 pb-1">
                      <p className="text-xs text-yellow-400">⚠ Unsaved changes</p>
                    </div>
                  )}
                  <div className="flex gap-3 p-4 pt-2">
                    <button
                      onClick={handleReset}
                      className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20 transition-colors"
                    >
                      Reset
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={!hasUnsavedChanges || isInvalid}
                      className={`flex-1 px-4 py-2 font-medium rounded-lg transition-colors ${
                        hasUnsavedChanges && !isInvalid
                          ? 'bg-white text-black hover:bg-gray-200'
                          : 'bg-white/20 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      )}

      {/* Mobile: Centered Modal */}
      {!showDesktopLayout && (
        <>
          <div>{trigger}</div>

          <AnimatePresence initial={false}>
            {isSettingsOpen && (
              <div
                key="mobile-settings-modal"
                className="fixed inset-0 flex items-center justify-center z-[200] p-2"
              >
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canClose) setSettingsOpen(false);
                  }}
                />

                {/* Modal Content */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{
                    opacity: 1, // Always visible when open
                    scale: 1,
                    y: 0,
                  }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className="relative z-[210] bg-gray-900/95 backdrop-blur-xl border-white/10 border rounded-2xl w-full max-w-sm max-h-[90vh] flex flex-col overflow-hidden"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {mobileView === 'list' ? (
                      <motion.div
                        key="list"
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -20, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col h-full overflow-hidden"
                      >
                        {/* List Header */}
                        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
                          <h2 className="text-lg font-bold text-white">Settings</h2>
                          <button
                            onClick={() => setSettingsOpen(false)}
                            className="p-2 hover:bg-white/10 rounded-full transition-colors"
                            aria-label="Close settings"
                          >
                            <X size={20} className="text-white" />
                          </button>
                        </div>

                        {/* List Content */}
                        <ScrollArea className="flex-1">
                          <div className="p-2 space-y-6">
                            {/* App Settings Group */}
                            <div>
                              <h3 className="px-4 text-xs font-bold text-gray-400 uppercase mb-2">
                                App Settings
                              </h3>
                              <div className="bg-white/5 rounded-xl overflow-hidden space-y-0.5">
                                {tabs
                                  .filter((t) =>
                                    [
                                      'timer',
                                      'appearance',
                                      'sounds',
                                      'notifications',
                                      'timezone',
                                    ].includes(t.id)
                                  )
                                  .map((tab) => {
                                    const Icon = tab.icon;
                                    return (
                                      <button
                                        key={tab.id}
                                        onClick={() => {
                                          setActiveTab(tab.id as typeof activeTab);
                                          setMobileView('detail');
                                        }}
                                        className="w-full flex items-center justify-between p-4 hover:bg-white/5 active:bg-white/10 transition-colors"
                                      >
                                        <div className="flex items-center gap-3">
                                          <Icon size={20} className="text-gray-400" />
                                          <span className="text-white font-medium">
                                            {tab.label}
                                          </span>
                                        </div>
                                        <ChevronRight size={16} className="text-gray-500" />
                                      </button>
                                    );
                                  })}
                              </div>
                            </div>

                            {/* Activity Group */}
                            <div>
                              <h3 className="px-4 text-xs font-bold text-gray-400 uppercase mb-2">
                                Activity
                              </h3>
                              <div className="bg-white/5 rounded-xl overflow-hidden space-y-0.5">
                                {tabs
                                  .filter((t) => ['progress', 'account'].includes(t.id))
                                  .map((tab) => {
                                    const Icon = tab.icon;
                                    return (
                                      <button
                                        key={tab.id}
                                        onClick={() => {
                                          setActiveTab(tab.id as typeof activeTab);
                                          setMobileView('detail');
                                        }}
                                        className="w-full flex items-center justify-between p-4 hover:bg-white/5 active:bg-white/10 transition-colors"
                                      >
                                        <div className="flex items-center gap-3">
                                          <Icon size={20} className="text-gray-400" />
                                          <span className="text-white font-medium">
                                            {tab.label}
                                          </span>
                                        </div>
                                        <ChevronRight size={16} className="text-gray-500" />
                                      </button>
                                    );
                                  })}
                              </div>
                            </div>

                            {/* Support & Info Group */}
                            <div>
                              <h3 className="px-4 text-xs font-bold text-gray-400 uppercase mb-2">
                                Support & Info
                              </h3>
                              <div className="bg-white/5 rounded-xl overflow-hidden space-y-0.5">
                                {tabs
                                  .filter((t) => ['whats-new', 'music', 'support'].includes(t.id))
                                  .map((tab) => {
                                    const Icon = tab.icon;
                                    return (
                                      <button
                                        key={tab.id}
                                        onClick={() => {
                                          setActiveTab(tab.id as typeof activeTab);
                                          setMobileView('detail');
                                        }}
                                        className="w-full flex items-center justify-between p-4 hover:bg-white/5 active:bg-white/10 transition-colors"
                                      >
                                        <div className="flex items-center gap-3">
                                          <Icon size={20} className="text-gray-400" />
                                          <span className="text-white font-medium">
                                            {tab.label}
                                          </span>
                                        </div>
                                        <ChevronRight size={16} className="text-gray-500" />
                                      </button>
                                    );
                                  })}
                              </div>
                            </div>
                          </div>
                        </ScrollArea>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="detail"
                        initial={{ x: 20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 20, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col h-full overflow-hidden"
                      >
                        {/* Detail Header */}
                        <div className="flex items-center gap-3 p-4 border-b border-white/10 shrink-0 bg-gray-900/50">
                          <button
                            onClick={() => setMobileView('list')}
                            className="p-1 -ml-2 text-gray-400 hover:text-white transition-colors"
                          >
                            <ChevronLeft size={24} />
                          </button>
                          <h2 className="text-lg font-bold text-white">
                            {tabs.find((t) => t.id === activeTab)?.label}
                          </h2>
                        </div>

                        {/* Detail Content */}
                        <ScrollArea className="flex-1 overflow-y-auto">
                          <div className="p-4 w-full max-w-full overflow-hidden">
                            <SettingsContent
                              activeTab={activeTab}
                              isMobile={true}
                              tempTimers={tempTimers}
                              setTempTimers={setTempTimers}
                              tempPomodorosBeforeLongBreak={tempPomodorosBeforeLongBreak}
                              setTempPomodorosBeforeLongBreak={setTempPomodorosBeforeLongBreak}
                              tempAutoStartBreaks={tempAutoStartBreaks}
                              setTempAutoStartBreaks={setTempAutoStartBreaks}
                              tempAutoStartPomodoros={tempAutoStartPomodoros}
                              setTempAutoStartPomodoros={setTempAutoStartPomodoros}
                              tempSoundEnabled={tempSoundEnabled}
                              setTempSoundEnabled={setTempSoundEnabled}
                              tempVolume={tempVolume}
                              setTempVolume={setTempVolume}
                              tempLevelSystemEnabled={tempLevelSystemEnabled}
                              setTempLevelSystemEnabled={setTempLevelSystemEnabled}
                              notificationPermission={notificationPermission}
                              tempBackground={tempBackground}
                              setTempBackground={setTempBackground}
                              tempAutoHideUI={tempAutoHideUI}
                              setTempAutoHideUI={setTempAutoHideUI}
                              filteredBackgrounds={filteredBackgrounds}
                              tempMusicVolume={tempMusicVolume}
                              setTempMusicVolume={setTempMusicVolume}
                              tempAmbientVolumes={tempAmbientVolumes}
                              setTempAmbientVolumes={setTempAmbientVolumes}
                              tempPlaylist={tempPlaylist}
                              setTempPlaylist={setTempPlaylist}
                              totalTracks={totalTracks}
                              setShowMusicCredits={setShowMusicCredits}
                              level={level}
                              xp={xp}
                              prestigeLevel={prestigeLevel}
                              totalPomodoros={totalPomodoros}
                              totalStudyMinutes={totalStudyMinutes}
                              levelPath={levelPath}
                              handleRoleChange={handleRoleChange}
                              usernameInput={usernameInput}
                              setUsernameInput={setUsernameInput}
                              usernameError={usernameError}
                              setUsernameError={setUsernameError}
                              usernameLoading={usernameLoading}
                              handleSaveUsername={handleSaveUsername}
                              resetProgress={resetProgress}
                              totalUniqueDays={totalUniqueDays}
                              consecutiveLoginDays={consecutiveLoginDays}
                              pomodoroBoostActive={pomodoroBoostActive}
                              pomodoroBoostExpiresAt={pomodoroBoostExpiresAt}
                              firstLoginDate={firstLoginDate}
                              timezone={timezone}
                              weekendDays={weekendDays}
                              pendingTimezone={pendingTimezone}
                              pendingTimezoneAppliesAt={pendingTimezoneAppliesAt}
                              lastTimezoneChangeAt={lastTimezoneChangeAt}
                              onClose={() => setSettingsOpen(false)}
                            />
                          </div>
                        </ScrollArea>

                        {/* Footer - Only show Save if we have changes */}
                        <div className="border-t border-white/10 shrink-0 p-4 bg-gray-900/50">
                          <button
                            onClick={handleSave}
                            disabled={!hasUnsavedChanges}
                            className={`w-full px-4 py-3 font-medium rounded-lg transition-colors ${
                              hasUnsavedChanges
                                ? 'bg-white text-black hover:bg-gray-200'
                                : 'bg-white/20 text-gray-500 cursor-not-allowed hidden' // Hide if no changes in mobile list view to keep it clean? No, let's keep it but maybe only show in detail or always?
                            }`}
                          >
                            Save Changes
                          </button>
                          {/* If no changes, maybe show a Close button or just rely on the X in the header? 
                              Actually the list view has an X. The detail view has a Back button.
                              Let's keep the Save button but maybe make it cleaner.
                           */}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Role Change Toast Notification */}
      {roleChangeMessage && (
        <div className="fixed bottom-6 right-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-4 rounded-lg shadow-2xl border-2 border-white/20 max-w-sm animate-slide-up z-[100]">
          <div className="flex items-start gap-3">
            <span className="text-3xl">
              {levelPath === 'elf' ? ROLE_EMOJI_ELF : ROLE_EMOJI_HUMAN}
            </span>
            <div>
              <p className="font-bold text-sm mb-1">Role Changed!</p>
              <p className="text-sm leading-relaxed">{roleChangeMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Music Credits Modal */}
      <AnimatePresence>
        {showMusicCredits && (
          <Suspense fallback={<LoadingSpinner />}>
            <MusicCreditsModal
              tracks={[...lofiTracks, ...synthwaveTracks] as Track[]}
              onClose={() => setShowMusicCredits(false)}
            />
          </Suspense>
        )}
      </AnimatePresence>
    </>
  );
});
